/**
 * Shared browser file-dialog helper used by the image-picker and
 * document-picker web shims.
 */

export type PickedWebFile = {
  file: File;
  uri: string;
  name: string;
  type: string;
  size: number;
};

export type FileDialogOptions = {
  accept?: string;
  multiple?: boolean;
};

/**
 * Opens the browser's file chooser and resolves with the chosen files.
 * Resolves with an empty array when the user dismisses the dialog.
 */
export function openFileDialog(options: FileDialogOptions = {}): Promise<PickedWebFile[]> {
  return new Promise(resolve => {
    if (typeof document === 'undefined') {
      resolve([]);
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.style.display = 'none';
    if (options.accept) {
      input.accept = options.accept;
    }
    input.multiple = Boolean(options.multiple);

    let settled = false;
    const finish = (files: PickedWebFile[]) => {
      if (settled) {
        return;
      }
      settled = true;
      if (input.parentNode) {
        input.parentNode.removeChild(input);
      }
      resolve(files);
    };

    input.addEventListener('change', () => {
      const list = Array.from(input.files || []);
      finish(
        list.map(file => ({
          file,
          uri: URL.createObjectURL(file),
          name: file.name,
          type: file.type,
          size: file.size,
        })),
      );
    });

    /*
     * There is no reliable "cancel" event across browsers. `cancel` is
     * supported in current Chrome/Edge; the window-focus fallback covers the
     * rest. Both funnel into finish(), which is idempotent.
     */
    input.addEventListener('cancel', () => finish([]));

    /*
     * The focus guess can misfire: window focus returns when the dialog
     * closes, but `change` for a large file or a network drive can land well
     * after that, and the pick was then thrown away as a cancel. Only use it
     * where the real `cancel` event does not exist (current Chromium, Electron,
     * Firefox and Safari all have it), and give `change` longer to arrive.
     */
    // Checked on a plain object: the DOM typings claim every input has it.
    const supportsCancel = 'oncancel' in (input as object);
    if (!supportsCancel) {
      const onFocus = () => {
        window.removeEventListener('focus', onFocus);
        window.setTimeout(() => {
          if (!input.files || input.files.length === 0) {
            finish([]);
          }
        }, 2000);
      };
      window.addEventListener('focus', onFocus);
    }

    document.body.appendChild(input);
    input.click();
  });
}

/** Reads a File as a bare base64 payload (no data: prefix). */
export function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/** Reads the intrinsic pixel size of an image file. */
export function readImageSize(uri: string): Promise<{width?: number; height?: number}> {
  return new Promise(resolve => {
    const image = new Image();
    image.onload = () => resolve({width: image.naturalWidth, height: image.naturalHeight});
    image.onerror = () => resolve({});
    image.src = uri;
  });
}
